import { useEffect, useRef } from "react"
import { MentoringsList } from "../MentoringsList/MentoringsList"
import { MentoringDataType } from "@/types/mentoring"
import { useModal } from "@/components/molecules/Modal/ModalProvider"
import RescheduleMentoringModal from "../MentoringBooking/RescheduleMentoringModal"
import { MentoringsFormModal } from "../MentoringsForm"
import { Company } from "@/types/company"
import { CompanySelectionModalContent } from "../MentoringsForm/CompanySelectionModalContent"
import { Button, Text } from "@/components/atoms"
import { useNavigate } from "react-router-dom"
import CancelScheduleMentoringModal from "../MentoringBooking/CancelScheduleMentoringModal"
import MentoringBooking from "../MentoringBooking"

interface TabMentoringsProps {
  companyId: string
  company?: Company | null
  mentoringsData: MentoringDataType[]
  onRefresh?: () => void
  onLoadMore?: (nextPage: number) => void // Callback para carregar a próxima página
  meta?: {
    page: number
    hasMore: boolean
    total: number
  }
}

export const CompaniesFormTabMentorings = ({
  companyId,
  company,
  mentoringsData,
  onRefresh,
  onLoadMore,
  meta,
}: TabMentoringsProps) => {
  const { showModal, hideModal } = useModal()
  const observerLoader = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // --- Infinite Scroll Logic ---
  useEffect(() => {
    // Se não houver mais dados ou função de carga, não ativa o observer
    if (!meta?.hasMore || !onLoadMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore(meta.page + 1)
        }
      },
      {
        threshold: 0.1,
        rootMargin: "200px", // Carrega quando estiver a 200px do fim
      }
    )

    if (observerLoader.current) {
      observer.observe(observerLoader.current)
    }

    return () => observer.disconnect()
  }, [meta, onLoadMore])

  // --- Handlers ---
  const handleFormSuccess = () => {
    hideModal()
    onRefresh?.()
  }

  const handleOpenEditModal = (mentoring: MentoringDataType) => {
    openFormModal(mentoring, null)
  }

  const handleSelectCompany = (company: Company) => {
    openFormModal(null, company)
  }

  const handleOpenCompanySelection = () => {
    showModal({
      title: "Selecione a empresa",
      size: "lg",
      content: <CompanySelectionModalContent onSelectCompany={handleSelectCompany} />,
    })
  }

  const openFormModal = (mentoring: MentoringDataType | null, company: Company | null) => {
    const isEditing = !!mentoring
    showModal({
      title: isEditing ? "Editar Mentoria" : "Nova Mentoria",
      size: "xl",
      content: (
        <MentoringsFormModal
          onSuccess={handleFormSuccess}
          onChangeCompany={handleOpenCompanySelection}
          initialData={mentoring ?? undefined}
          companyData={company}
        />
      ),
    })
  }

  const handleOpenRescheduleModal = (mentoring: MentoringDataType) => {
    showModal({
      title: "Reagendar Mentoria",
      content: (
        <RescheduleMentoringModal
          mentoring={mentoring}
          onSuccess={handleFormSuccess}
        />
      ),
      size: "xl",
    })
  }

  const handleOpenCancelModal = (mentoring: MentoringDataType) => {
    showModal({
      title: "Cancelar Mentoria",
      content: (
        <CancelScheduleMentoringModal
          mentoring={mentoring}
          onSuccess={handleFormSuccess}
        />
      ),
      size: "xl",
    })
  }

  const handleOpenBookingModal = () => {
    if (!company) return

    showModal({
      title: "Agendar Mentoria",
      size: "xl",
      content: (
        <MentoringBooking
          company={company}
          onSuccess={() => {
            hideModal()
            onRefresh?.()
          }}
        />
      ),
    })
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-end px-8 pt-4">
        <Button
          icon="calendar"
          disabled={!company || company.statusEmpresa === 0}
          onClick={handleOpenBookingModal}
        >
          Agendar Mentoria
        </Button>
      </div>

      {mentoringsData.length === 0 ? (
        <section className="flex h-[300px] items-center justify-center">
          <h4 className="text-center text-dark-300">Nenhuma mentoria encontrada</h4>
        </section>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-4 px-8 py-4">
          {mentoringsData.map((mentoring) => (
            <MentoringsList
              className="border border-white/5 bg-dark-400 shadow-xl"
              key={mentoring.mentoriaId || mentoring.mentoria_id}
              onCardClick={() => navigate(`/mentorings/${mentoring.mentoriaId}/${mentoring.empId}`)}
              onRescheduleClick={() => handleOpenRescheduleModal(mentoring)}
              onCancelScheduleClick={() => handleOpenCancelModal(mentoring)}
              mentoring={mentoring}
            />
          ))}

          {/* Sentinel - O "imã" que puxa a próxima página */}
          <div
            ref={observerLoader}
            className="col-span-full flex h-16 items-center justify-center pt-4"
          >
            {meta?.hasMore ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <Text
                  variant="body"
                  className="text-dark-300"
                >
                  Carregando mais...
                </Text>
              </div>
            ) : (
              <Text
                variant="body"
                className="opacity-30"
              >
                {mentoringsData.length} mentorias carregadas no total
              </Text>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
